import { useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, Loader2, MapPin, Upload } from 'lucide-react'
import { Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import { useCan } from '@/shared/auth/session'
import { fmtNumber } from '@/shared/lib/format'
import { UnitMap } from '@/shared/map/UnitMap'
import { countPoints, extractRings } from '@/shared/map/geo'
import { getBoundary, uploadBoundary } from './api'
import { UNIT_TYPE_LABEL, geometryKindOf, type GeoJsonObject, type UnitResponse } from '@/shared/api/types'

/**
 * Geometry, for both kinds of unit.
 *
 * A county, constituency or ward is an **area**: this panel shows its polygon on
 * a basemap and accepts a replacement GeoJSON. A polling station is a **place**:
 * it has no polygon at all — the backend rejects one outright — so the panel
 * shows its pin instead of an upload control it could never use.
 *
 * The basemap is Mapbox, loaded lazily (see `shared/map/mapbox`). When no token
 * is configured the panel degrades to the inline vector outline, which needs no
 * network and no account. That fallback is deliberately kept: it is what runs in
 * CI, offline, and during a token rotation.
 */
export function BoundaryPanel({
  unit, version, onChanged,
}: {
  unit: UnitResponse
  version: number
  onChanged: () => void
}) {
  const canUpdate = useCan('geography:update')
  const kind = unit.geometryKind ?? geometryKindOf(unit.unitType)
  const [geo, setGeo] = useState<GeoJsonObject | null>(null)
  const [loading, setLoading] = useState(kind === 'BOUNDARY')
  const [missing, setMissing] = useState(false)
  const [note, setNote] = useState<{ tone: 'good' | 'bad'; text: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (kind !== 'BOUNDARY') { setGeo(null); setLoading(false); return }
    let cancelled = false
    setLoading(true)
    setMissing(false)
    setGeo(null)
    // A tolerance of ~0.001° (roughly 100 m) is plenty for a detail pane and keeps
    // a county boundary from arriving as tens of thousands of vertices.
    getBoundary(unit.id, 0.001)
      .then((g) => { if (!cancelled) setGeo(g) })
      .catch(() => {
        // A unit with no boundary yet is the normal case, not an error.
        if (!cancelled) setMissing(true)
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
    // `version` changes after an upload, which is the signal to refetch.
  }, [unit.id, kind, version])

  async function onPick(file: File) {
    setBusy(true)
    setNote(null)
    try {
      const text = await file.text()
      let parsed: GeoJsonObject
      try {
        parsed = JSON.parse(text) as GeoJsonObject
      } catch {
        throw new Error('That file is not valid JSON.')
      }
      await uploadBoundary(unit.id, parsed)
      setNote({ tone: 'good', text: 'Boundary saved. The pin and area were recomputed from it.' })
      onChanged()
    } catch (err) {
      setNote({ tone: 'bad', text: explain(err) })
    } finally {
      setBusy(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const rings = useMemo(() => extractRings(geo), [geo])
  const typeLabel = (UNIT_TYPE_LABEL[unit.unitType] ?? unit.unitType).toLowerCase()

  // ── Point-based: a station ────────────────────────────────────────────────
  if (kind === 'POINT') {
    const hasPoint = unit.latitude != null && unit.longitude != null
    return (
      <div className="px-4 py-3 space-y-3">
        <UnitMap
          mode="point"
          lat={unit.latitude ?? null}
          lon={unit.longitude ?? null}
          label={unit.name}
          className="h-56"
        />
        {hasPoint ? (
          <p className="text-erp-xs text-muted-foreground font-mono">
            {unit.latitude}, {unit.longitude}
          </p>
        ) : (
          <Note tone="info">
            This station has no coordinates, so it will never appear in nearby search
            or on a map. Add them from Edit.
          </Note>
        )}
        <p className="text-erp-xs text-muted-foreground flex items-start gap-1.5">
          <MapPin className="h-3 w-3 mt-0.5 shrink-0" />
          A polling station is a point, not an area — the backend rejects a boundary on
          one. Its coordinates are edited on the unit itself.
        </p>
      </div>
    )
  }

  // ── Boundary-based: county, constituency, ward ────────────────────────────
  return (
    <div className="px-4 py-3 space-y-3">
      {note && <Note tone={note.tone} onDismiss={() => setNote(null)}>{note.text}</Note>}

      {loading ? (
        <div className="py-8 text-center text-erp-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading boundary…
        </div>
      ) : rings.length > 0 ? (
        <>
          <UnitMap mode="boundary" boundary={geo} label={unit.name} className="h-64" />
          <div className="flex items-center justify-between gap-3 flex-wrap text-erp-xs text-muted-foreground">
            <span>
              {fmtNumber(countPoints(rings))} points, simplified for display.
              The stored geometry is exact.
            </span>
            {unit.areaSqKm != null && (
              <span className="erp-metric">{fmtNumber(Number(unit.areaSqKm))} km²</span>
            )}
          </div>
        </>
      ) : (
        <div className="border border-dashed border-border rounded-md px-4 py-8 text-center">
          <p className="text-erp-sm text-muted-foreground">
            {missing ? `This ${typeLabel} has no boundary yet.` : 'No boundary geometry.'}
          </p>
          <p className="text-erp-xs text-muted-foreground mt-1">
            A {typeLabel} is an area. Without a polygon it cannot be drawn on a map,
            and containment questions have to fall back to the code hierarchy.
          </p>
        </div>
      )}

      {canUpdate && (
        <>
          <input
            ref={fileRef}
            type="file"
            accept=".json,.geojson,application/json,application/geo+json"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) void onPick(f)
            }}
          />
          <button
            type="button"
            className="erp-btn"
            disabled={busy}
            onClick={() => fileRef.current?.click()}
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            {rings.length > 0 ? 'Replace boundary' : 'Upload GeoJSON'}
          </button>
          <p className="text-erp-xs text-muted-foreground flex items-start gap-1.5">
            <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
            One unit at a time, sent as the request body — the file is not stored, so the
            original is not downloadable afterwards, and there is no way to remove a
            boundary once set. Bulk import from a single IEBC FeatureCollection, file
            provenance and a delete are all in backend prompt 9. Coordinates must be
            WGS84 (EPSG:4326).
          </p>
        </>
      )}

    </div>
  )
}
